import { Link } from "react-router-dom";
import { Shield, Check, Phone, Star, MapPin, Clock, FileText } from "lucide-react";
import PestOnlyCalculator from "../components/PestOnlyCalculator";
import { Logo } from "../components/Layout";
import { Header } from "../components/Layout";
import IpswichBreadcrumb from "../components/IpswichBreadcrumb";

const suburbs = ["Ipswich CBD", "Brassall", "Booval", "Bundamba", "Goodna", "Redbank Plains", "Collingwood Park", "Bellbird Park", "Ripley", "Springfield", "North Ipswich", "East Ipswich", "Leichhardt", "Silkstone", "Yamanto"];

export default function EndOfLeasePestControlIpswichPage() {
  return (
    <>
      <>
        <title>End of Lease Pest Control Ipswich | Bond Back Guarantee | Pest Free</title>
        <meta name="description" content="End of lease pest control in Ipswich QLD. Get your bond back with our professional pest treatment & certificate. Same-day service, competitive prices. QBCC licensed." />
        <link rel="canonical" href="https://pestfreebrisbane.com.au/end-of-lease-pest-control-ipswich" />
      </>

      <Header />
      <div className="min-h-screen bg-slate-50">
        <IpswichBreadcrumb current="End of Lease Pest Control" />
        <section className="bg-gradient-to-br from-[#1B5E20] via-[#2E7D32] to-[#7ABC32]/20 text-white pt-28 pb-16 md:pt-32 md:pb-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#7ABC32]/20 border border-[#7ABC32]/30 rounded-full px-4 py-1.5 mb-5">
                  <MapPin className="w-4 h-4 text-[#7ABC32]" />
                  <span className="text-sm font-medium text-[#7ABC32]">Ipswich QLD</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight" data-testid="end-of-lease-h1">
                  End of Lease Pest Control <span className="text-[#7ABC32]">Ipswich</span>
                </h1>
                <p className="text-lg text-slate-200 mb-6">
                  Moving out of your Ipswich rental? Most leases require a professional pest treatment before you hand back the keys. We provide fast, affordable end-of-lease pest control with a certificate to help you get your full bond back.
                </p>
                <div className="flex flex-wrap gap-3 mb-6">
                  <div className="flex items-center gap-1.5 text-sm text-slate-200"><Check className="w-4 h-4 text-[#7ABC32]" />Certificate Provided</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-200"><Check className="w-4 h-4 text-[#7ABC32]" />Same Day Service</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-200"><Check className="w-4 h-4 text-[#7ABC32]" />Bond-Back Compliant</div>
                </div>
                <div className="flex items-center gap-3">
                  <a href="tel:0737081422" className="flex items-center gap-2 bg-[#7ABC32] hover:bg-[#6AAD28] text-white font-semibold px-6 py-3 rounded-full transition-all" data-testid="end-of-lease-call-btn">
                    <Phone className="w-5 h-5" /> (07) 3708 1422
                  </a>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                    <span className="text-sm text-slate-200 ml-1">4.9 on Google</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-center">
                <Logo className="h-32 md:h-40 opacity-90" />
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16" id="calculator">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800">Get Your End of Lease Quote</h2>
              <p className="text-slate-600 mt-2">Instant pricing — includes pest treatment certificate</p>
            </div>
            <PestOnlyCalculator source="end_of_lease_ipswich" />
          </div>
        </section>

        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-3">What's Included in End of Lease Pest Control</h2>
            <p className="text-slate-600 text-center mb-10 max-w-3xl mx-auto">Our end-of-lease treatment meets the requirements of all major Ipswich property managers and real estate agencies.</p>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: "Full Internal & External Spray", desc: "Professional treatment of all rooms, skirting boards, cupboards, wet areas and external perimeter. Covers cockroaches, ants, spiders and silverfish.", icon: Shield, color: "#7ABC32" },
                { title: "Pest Certificate", desc: "You'll receive a professional pest treatment certificate immediately after the service. This is what your property manager needs to release your bond.", icon: FileText, color: "#5BC0DE" },
                { title: "Same Day Booking", desc: "Moving out tomorrow? We offer same-day and next-day bookings across all Ipswich suburbs. We understand end-of-lease timelines are tight.", icon: Clock, color: "#E74C3C" },
              ].map((item, i) => (
                <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-200 text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: item.color + '15' }}>
                    <item.icon className="w-6 h-6" style={{ color: item.color }} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">How It Works</h2>
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { step: "1", title: "Get a Quote", desc: "Use our calculator above or call us for an instant price." },
                { step: "2", title: "Book Your Time", desc: "Pick a date that works with your move-out schedule. Same-day available." },
                { step: "3", title: "We Treat", desc: "Our technician treats the entire property inside and out. Takes 45-90 mins." },
                { step: "4", title: "Get Certificate", desc: "Receive your pest certificate on the spot. Send it straight to your agent." },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-5 border border-slate-200 text-center">
                  <div className="w-9 h-9 rounded-full bg-[#7ABC32] text-white font-bold text-sm flex items-center justify-center mx-auto mb-3">{item.step}</div>
                  <h3 className="font-bold text-slate-800 text-sm mb-1">{item.title}</h3>
                  <p className="text-xs text-slate-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">End of Lease Pest Control FAQ — Ipswich</h2>
            <div className="space-y-4">
              {[
                { q: "Is end of lease pest control required in Queensland?", a: "If your lease agreement includes a pest control clause (most do in Ipswich), you're required to have a professional pest treatment done before handing back the property. Without it, your property manager can deduct the cost from your bond — often at a higher rate." },
                { q: "How much does end of lease pest control cost in Ipswich?", a: "End of lease pest control in Ipswich typically starts from around $165 for a 1-bedroom property. Use our calculator above for an instant quote based on your property size and type." },
                { q: "Do you provide a certificate?", a: "Yes. Every end-of-lease treatment includes a professional pest treatment certificate. It's provided on-site as soon as the treatment is complete. This is the document your real estate agent or property manager needs." },
                { q: "How quickly can you come?", a: "We offer same-day and next-day bookings across all Ipswich suburbs. We know end-of-lease dates are fixed, so we prioritise these jobs to fit your schedule." },
                { q: "What pests are covered?", a: "Our standard end-of-lease treatment covers cockroaches, ants, spiders, silverfish and webbing. Rodent treatment and flea treatment can be added if required by your lease agreement." },
                { q: "Can I be in the property during treatment?", a: "We recommend vacating the property for 30-60 minutes after treatment to allow the product to dry. It's best to schedule the treatment after your final clean and before key handover." },
              ].map((item, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                  <h3 className="font-bold text-slate-800 mb-2">{item.q}</h3>
                  <p className="text-sm text-slate-600">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">End of Lease Pest Control Across Ipswich</h2>
            <div className="flex flex-wrap justify-center gap-2">
              {suburbs.map(s => (
                <span key={s} className="bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700">{s}</span>
              ))}
            </div>
            <p className="text-slate-500 text-sm mt-4">And all surrounding areas in the Ipswich region</p>
          </div>
        </section>

        <section className="py-8 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-lg font-bold text-slate-800 text-center mb-4">Related Services in Ipswich</h2>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/pest-control-ipswich" className="text-sm text-[#E74C3C] hover:underline font-medium">Pest Control Ipswich</Link>
              <span className="text-slate-300">|</span>
              <Link to="/cockroach-treatment-ipswich" className="text-sm text-[#E74C3C] hover:underline font-medium">Cockroach Treatment Ipswich</Link>
              <span className="text-slate-300">|</span>
              <Link to="/cheap-pest-control-ipswich" className="text-sm text-[#7ABC32] hover:underline font-medium">Cheap Pest Control Ipswich</Link>
              <span className="text-slate-300">|</span>
              <Link to="/termite-inspection-ipswich" className="text-sm text-[#D2691E] hover:underline font-medium">Termite Inspection Ipswich</Link>
              <span className="text-slate-300">|</span>
              <Link to="/bird-control-ipswich" className="text-sm text-[#5BC0DE] hover:underline font-medium">Bird Control Ipswich</Link>
            </div>
          </div>
        </section>

        <section className="py-12 bg-gradient-to-r from-[#1B5E20] to-[#7ABC32] text-white text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Get Your Bond Back — Book Today</h2>
            <p className="text-lg text-white/80 mb-6">Fast, affordable end-of-lease pest control with certificate. Same-day service across Ipswich.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:0737081422" className="inline-flex items-center gap-2 bg-white text-[#1B5E20] font-bold px-8 py-3 rounded-full hover:bg-slate-100 transition-all">
                <Phone className="w-5 h-5" /> Call (07) 3708 1422
              </a>
              <a href="#calculator" className="inline-flex items-center gap-2 border-2 border-white text-white font-bold px-8 py-3 rounded-full hover:bg-white/10 transition-all">
                <Clock className="w-5 h-5" /> Get Instant Quote
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
